import React from 'react';
import PropTypes from 'prop-types';
import {
  isEmpty,
  cloneDeep,
  remove,
  map,
  union,
  uniqBy,
  omit,
  size,
  findIndex,
  findLastIndex,
  isObject,
  sortBy,
} from 'lodash';
import { toast } from 'react-toastify';
import { withTranslation } from 'react-i18next';
import CustomerNoForm from '../CustomerNoForm';
import { ModalWithItem } from '../../common';
import ModalAccept from '../ModalAccept';
import FormAddNewContact from '../FormAddNewContact';
import { isCheckValidateForm, messageRoleBilling, validate } from '../../../utils/utils';
import { keepStoredOption, getCcpList } from '../../../utils/tenantFieldConfig';

// The engine numbers a new account's first contact PRIMARY: the account holder the top-up receipt names
const PRIMARY_CONTACT_ID = 'PRIMARY';

/* A tenant that hides the contact id has no other sign of which contact is the account holder: it is
 * listed first and tagged */
export const marksPrimaryContact = ccpPropertyList =>
  getCcpList(ccpPropertyList, 'contactHiddenFields').indexOf('id') > -1;

/* A tenant whose contact ids the engine numbers ('id' in contactOptionalFields) keeps its PRIMARY contact: the
 * engine deletes it only in the save that makes another contact main, so its Delete button is off until then */
export const keepsPrimaryContact = ccpPropertyList =>
  getCcpList(ccpPropertyList, 'contactOptionalFields').indexOf('id') > -1;

/* The PRIMARY contact first, the others in the order they came; a new array, the input is left as it is */
export const primaryContactFirst = (contacts, ccpPropertyList) =>
  marksPrimaryContact(ccpPropertyList) && Array.isArray(contacts)
    ? sortBy(contacts, contact => (contact && contact.id === PRIMARY_CONTACT_ID ? 0 : 1))
    : contacts;

/* The cards as shown: the PRIMARY first where it is tagged, each phones switch on when phones are stored */
const cardsFrom = (contacts, ccpPropertyList) => {
  const sorted = primaryContactFirst(contacts, ccpPropertyList);
  return Array.isArray(sorted) ? sorted.map(val => ({ ...val, isShowPhones: !!val.phones })) : sorted;
};

/* The phones one contact sends: the ones it shows, and a bare type for each phone removed since the last
 * save (the engine deletes a phone sent without a number). A type shown again goes with its number. */
export const phonesToSend = (shownPhones, sentPhones) => {
  const shown = shownPhones || [];
  const removed = (sentPhones || []).filter(
    phone =>
      phone &&
      phone.type &&
      !Object.prototype.hasOwnProperty.call(phone, 'number') &&
      !shown.some(item => item && item.type === phone.type)
  );
  return [...shown, ...uniqBy(removed, 'type').map(({ type }) => ({ type }))];
};

/* What one contact sends on Modify, or null for nothing. The engine reads an entry it would write
 * nothing for as a delete, so only the Delete button sends a bare id. The phones switch only shows or
 * hides the phones: switched off, the stored phones stay as they are. An empty field the engine does not
 * clear (one not in contactOptionalFields) is left out, as the engine would ignore it anyway. */
export const contactEntryToSend = (changes, contact, optionalFields = []) => {
  const { isShowPhones, ...entry } = changes || {};
  // a card added a moment ago may not be in the list yet: its changes are the whole card
  const current = contact || entry;
  const id = entry.id || current.id || null;
  // Deleted with the Delete button: the card holds only its id (a card never saved has nothing to delete)
  if (size(current) < 2) return current.id ? { id } : null;
  if (isShowPhones === false) delete entry.phones;
  if (current.id) {
    Object.keys(entry).forEach(key => {
      if (key === 'id') return;
      if (entry[key] === null || entry[key] === undefined) delete entry[key];
      // the engine never clears firstName (NOT NULL): an empty one, even where listed, would read as a delete
      else if (entry[key] === '' && (optionalFields.indexOf(key) === -1 || key === 'firstName')) delete entry[key];
    });
    if (isEmpty(omit(entry, 'id'))) return null;
  }
  return { ...entry, id };
};

class MultiContactForm extends React.PureComponent {
  state = {
    accountContact: [],
    dataSubmit: [],
    wasValidated: false,
    isOpenModalNewContact: false,
    isOpenModalDeleteContact: false,
    indexDeleteContact: 0,
    isModify: false,
    // A saved contact the user made the main one; it becomes PRIMARY when the change is saved
    primaryContactId: null,
    // The card that got the billing role because it was made main: { index, roles before, roles it was to send }
    billingAdded: null,
  };

  formRef = React.createRef();

  componentDidMount() {
    const { data, ccpPropertyList } = this.props;
    if (data) {
      this.setState({ accountContact: cardsFrom(data, ccpPropertyList) });
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.data) {
      // Sorted here, not in render: the edits and the submit follow the card positions
      this.setState({
        accountContact: cardsFrom(nextProps.data, nextProps.ccpPropertyList),
        dataSubmit: [],
        primaryContactId: null,
        billingAdded: null,
      });
    }
  }

  onToggleModalNewContact = () => {
    const { isOpenModalNewContact } = this.state;
    this.setState({ isOpenModalNewContact: !isOpenModalNewContact });
  };

  onToggleModalDeleteContact = index => {
    const { isOpenModalDeleteContact } = this.state;
    this.setState({ isOpenModalDeleteContact: !isOpenModalDeleteContact, indexDeleteContact: index });
  };

  // The contact that is the main one once the change is saved
  mainContactId = () => {
    const { primaryContactId } = this.state;
    return primaryContactId || PRIMARY_CONTACT_ID;
  };

  onDeleteContact = () => {
    const { accountContact, indexDeleteContact, dataSubmit } = this.state;
    const { ccpPropertyList } = this.props;
    const contact = accountContact[indexDeleteContact];
    // A tenant that keeps a main contact deletes the current one only once another is made main
    if (!contact || (keepsPrimaryContact(ccpPropertyList) && contact.id === this.mainContactId())) return;
    const newData = cloneDeep(accountContact);
    dataSubmit[indexDeleteContact] = { id: accountContact[indexDeleteContact].id };
    newData[indexDeleteContact] = { id: accountContact[indexDeleteContact].id };
    this.setState({ accountContact: newData, isModify: true });
  };

  onHandleChange = ({ value = '', name = '', index }) => {
    const { accountContact, dataSubmit } = this.state;
    const newData = cloneDeep(accountContact);
    let { isModify } = this.state;
    // With contactOptionalFields, a cleared field counts only where the engine writes it empty;
    // any other field sent empty on its own would make the contact read as a delete.
    const optional = getCcpList(this.props.ccpPropertyList, 'contactOptionalFields');
    if (optional.length ? value !== newData[index][name] && (value || optional.indexOf(name) > -1) : value) {
      isModify = true;
    }
    newData[index][name] = value;
    if (!isObject(dataSubmit[index])) dataSubmit[index] = {};
    dataSubmit[index][name] = value;
    this.setState({ accountContact: newData, ...dataSubmit, isModify });
  };

  onChangeRole = (name, index) => {
    const { accountContact, dataSubmit, billingAdded } = this.state;
    const newData = cloneDeep(accountContact);
    if (newData[index].roles.indexOf(name) > -1) {
      remove(newData[index].roles, item => item === name);
    } else {
      newData[index].roles.push(name);
    }
    if (newData[index]?.roles?.length === 0) return '';
    if (!isObject(dataSubmit[index])) dataSubmit[index] = {};
    dataSubmit[index].roles = newData[index].roles;
    return this.setState({
      accountContact: newData,
      ...dataSubmit,
      isModify: true,
      // roles set by hand on the card made main are the user's: undoing the main choice leaves them
      billingAdded: billingAdded && billingAdded.index === index ? null : billingAdded,
    });
  };

  /* Making the PRIMARY contact main again undoes the choice. The contact made main gets the billing role, ticked
   * on its card: the e-invoice names the main contact only when it is a billing contact. The user can untick it
   * before saving. Undoing the choice puts back the roles the card had then, and what it was about to send; once
   * the user changes that card's roles by hand they are the user's, and the undo leaves them. */
  onMakePrimaryContact = index => {
    const { accountContact, dataSubmit, billingAdded } = this.state;
    const contact = accountContact[index];
    if (!contact || !contact.id) return;
    const newData = cloneDeep(accountContact);
    if (billingAdded && newData[billingAdded.index]) {
      newData[billingAdded.index].roles = billingAdded.roles;
      const entry = dataSubmit[billingAdded.index];
      if (isObject(entry)) {
        if (billingAdded.sentRoles) entry.roles = billingAdded.sentRoles;
        else delete entry.roles;
      }
    }
    const makesOther = contact.id !== PRIMARY_CONTACT_ID;
    const roles = newData[index].roles || [];
    let added = null;
    if (makesOther && roles.indexOf('BILLING') === -1) {
      added = { index, roles, sentRoles: isObject(dataSubmit[index]) ? dataSubmit[index].roles : undefined };
      newData[index].roles = [...roles, 'BILLING'];
      if (!isObject(dataSubmit[index])) dataSubmit[index] = {};
      dataSubmit[index].roles = newData[index].roles;
    }
    this.setState({
      accountContact: newData,
      dataSubmit,
      primaryContactId: makesOther ? contact.id : null,
      billingAdded: added,
      isModify: true,
    });
  };

  // The phone types the database holds for a saved card (none for a card not saved yet)
  storedPhoneTypes = indexContact => {
    const { data } = this.props;
    const { accountContact } = this.state;
    const id = accountContact[indexContact] && accountContact[indexContact].id;
    const stored = id && Array.isArray(data) ? data.find(contact => contact && contact.id === id) : null;
    return ((stored && stored.phones) || []).map(phone => phone && phone.type).filter(Boolean);
  };

  onAddNewContact = item => {
    const { accountContact, dataSubmit } = this.state;
    const newData = cloneDeep(accountContact);
    newData.push(item);
    dataSubmit[newData.length - 1] = newData[newData.length - 1];
    this.setState({ accountContact: newData, isOpenModalNewContact: false, dataSubmit, isModify: true });
  };

  onHandleSubmit = evt => {
    evt.preventDefault();
    // Only this form's own submit saves: a modal form rendered inside it (Add new contact) bubbles its submit here
    // in React, before the new card is in the list
    if (this.formRef.current && evt.target && evt.target !== this.formRef.current) return false;
    const { dataSubmit, accountContact, primaryContactId } = this.state;
    const { modifyAccount, id, t, doGetAccountDetails, ccpPropertyList } = this.props;
    const newDataSubmit = [];
    this.setState({ wasValidated: true });
    this.setState({ wasValidated: true });
    if (!validate(true, this.formRef, t)) {
      return false;
    }

    this.setState({ wasValidated: false });
    const messageRoleContact = messageRoleBilling({
      roles: union(
        Array.prototype.concat.apply(
          [],
          accountContact.map(item => item.roles)
        )
      ),
    });
    if (isCheckValidateForm() || messageRoleContact) return toast.error(t('message.mandatory'));

    const optional = getCcpList(ccpPropertyList, 'contactOptionalFields');
    if (!isEmpty(dataSubmit)) {
      for (let i = 0; i < dataSubmit.length; i++) {
        if (!isEmpty(dataSubmit[i])) {
          const entry = contactEntryToSend(dataSubmit[i], accountContact[i], optional);
          if (entry) newDataSubmit.push(entry);
        }
      }
    }
    const newPayload = {
      id,
      contacts: newDataSubmit,
    };
    // The engine applies the contact changes first, then makes this contact PRIMARY
    if (primaryContactId) newPayload.primaryContactId = primaryContactId;
    return modifyAccount(newPayload, ({ success }) => {
      if (success) {
        doGetAccountDetails();
      }
      this.setState({ wasValidated: false });
    });
  };

  onChangePhone = ({ value, fieldName, index, isNewPhone = false, isRemovePhone = false, indexContact }) => {
    const { accountContact, dataSubmit } = this.state;
    const newData = cloneDeep(accountContact);
    if (isNewPhone) {
      const typePhoneArr = ['MOBILE', 'WORK', 'HOME', 'FAX'];
      map(newData[indexContact].phones, item => {
        const index = typePhoneArr.indexOf(item.type);
        typePhoneArr.splice(index, 1);
      });
      if (isEmpty(typePhoneArr)) {
        return toast('No unique type phone remaining', { type: toast.TYPE.ERROR });
      }
      newData[indexContact].phones[newData[indexContact].phones.length] = {
        number: null,
        type: typePhoneArr[0],
      };
    }
    // The card's other edits stay, and each removal of a stored phone is sent until the save
    const stored = this.storedPhoneTypes(indexContact);
    if (isRemovePhone) {
      if (!isObject(dataSubmit[indexContact])) dataSubmit[indexContact] = {};
      const { type } = newData[indexContact].phones[index];
      newData[indexContact].phones.splice(index, 1);
      dataSubmit[indexContact].phones = phonesToSend(newData[indexContact].phones, [
        ...(dataSubmit[indexContact].phones || []),
        ...(stored.indexOf(type) > -1 ? [{ type }] : []),
      ]);
    }
    if (!isEmpty(newData[indexContact].phones) && !isNewPhone && !isRemovePhone) {
      const phone = newData[indexContact].phones[index];
      // A stored phone given another type leaves its former type behind unless that one is removed too
      const formerType =
        fieldName === 'type' && phone.type !== value && stored.indexOf(phone.type) > -1 ? [{ type: phone.type }] : [];
      phone[fieldName] = value;
      if (!isObject(dataSubmit[indexContact])) dataSubmit[indexContact] = {};
      dataSubmit[indexContact].phones = phonesToSend(newData[indexContact].phones, [
        ...(dataSubmit[indexContact].phones || []),
        ...formerType,
      ]);
    }
    return this.setState({ accountContact: newData, isModify: true, dataSubmit });
  };

  onChangeIsShowPhone = ({ index }) => {
    const { accountContact, dataSubmit } = this.state;
    const newData = cloneDeep(accountContact);
    try {
      newData[index].isShowPhones = !newData[index].isShowPhones;
      if (!isObject(dataSubmit[index])) dataSubmit[index] = {};
      dataSubmit[index].isShowPhones = newData[index].isShowPhones;
      this.setState({ accountContact: newData, isModify: true, dataSubmit });
    } catch (error) {
      console.log(error);
    }
  };

  render() {
    const {
      id,
      dataField,
      dataFieldNew,
      checkRoleGroup,
      isDisabled,
      isDisableSubmit,
      modeModifyPhone,
      modeAddPhone,
      modeDeletePhone,
      modeDeleteContact,
      modeAddContact,
      doGetAccountDetails,
      t,
      customerInfo,
      currencyOptions,
    } = this.props;
    const { accountContact, wasValidated, isOpenModalNewContact, isOpenModalDeleteContact, isModify } = this.state;
    if (isEmpty(accountContact)) return '';
    const messageRoleContact = messageRoleBilling({
      roles: union(
        Array.prototype.concat.apply(
          [],
          accountContact.map(item => item.roles)
        )
      ),
    });
    const isDisableDelete = accountContact.filter(item => size(item) > 2).length < 2;
    const indexRenderButton = findLastIndex(accountContact, item => size(item) > 2);
    const { ccpPropertyList } = this.props;
    const keepPrimary = keepsPrimaryContact(ccpPropertyList);
    const markPrimary = marksPrimaryContact(ccpPropertyList);
    // The section title and account header go on the first card still shown (the primary may be deleted
    // in this session by a tenant that does not keep it)
    const indexTitle = markPrimary ? findIndex(accountContact, item => size(item) >= 2) : 0;
    // Where the main contact is tagged and kept, another saved contact can be made the main one
    const mainId = this.mainContactId();
    const canChangeMain = keepPrimary && markPrimary;

    return (
      <form
        onSubmit={this.onHandleSubmit}
        className={`needs-validation ${wasValidated ? 'was-validated' : ''}`}
        ref={this.formRef}
        noValidate
      >
        {accountContact.map((item, index) => {
          if (size(item) < 2) return null;
          return (
            <CustomerNoForm
              key={index}
              colItem="col-md-4"
              dataField={dataField.map(field => keepStoredOption(field, item))}
              title={index === indexTitle ? t('label.contacts') : ''}
              accountNum={index === indexTitle ? id : ''}
              tag={markPrimary && item.id === mainId ? t('label.primaryContact') : ''}
              accountDetail={item}
              onHandleChange={({ name, value }) => this.onHandleChange({ name, value, index })}
              onHandleSubmit={this.onHandleSubmit}
              wasValidated={wasValidated}
              checkRoleGroup={checkRoleGroup}
              onChangePhone={({ ...props }) => this.onChangePhone({ ...props, indexContact: index })}
              onChangeCheckBox={name => this.onChangeRole(name, index)}
              isDisabled={isDisabled}
              modeModifyPhone={modeModifyPhone}
              modeAddPhone={modeAddPhone}
              modeDeletePhone={modeDeletePhone}
              messageRole={messageRoleContact}
              t={t}
              customerInfo={customerInfo}
              currencyOptions={currencyOptions}
              isLeftAddPhoneBtn
              isShowPhones={item.isShowPhones}
              showPhoneName={index}
              onChangeIsShowPhone={() => this.onChangeIsShowPhone({ index })}
            >
              {modeDeleteContact !== 0 && (
                <div className="form-group col-md-12">
                  <button
                    type="button"
                    className="button button-border black x-small float-right mr-4"
                    onClick={() => this.onToggleModalDeleteContact(index)}
                    disabled={isDisableDelete || modeDeleteContact === 1 || (keepPrimary && item.id === mainId)}
                  >
                    {t('label.delete')}
                  </button>
                </div>
              )}
              {canChangeMain && item.id && item.id !== mainId && (
                <div className="form-group col-md-12">
                  <button
                    type="button"
                    className="button button-border black x-small float-right mr-4"
                    onClick={() => this.onMakePrimaryContact(index)}
                    disabled={isDisabled}
                  >
                    {t('label.makePrimaryContact')}
                  </button>
                </div>
              )}
              {index === indexRenderButton && (
                <div className="form-group col-md-12 btn-add-new">
                  <div className="form-group col-md-12 action-buttons">
                    {modeAddContact !== 0 && (
                      <button
                        type="button"
                        className="button x-small"
                        onClick={this.onToggleModalNewContact}
                        disabled={modeAddContact === 1}
                      >
                        {t('label.addNewContact')}
                      </button>
                    )}
                    {!isDisableSubmit && (
                      <button
                        disabled={isDisableSubmit || !isModify}
                        type="submit"
                        className="button button-border x-small float-right"
                      >
                        {t('label.modify')}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={doGetAccountDetails}
                      className="button button-border black x-small float-right"
                    >
                      {t('label.cancel')}
                    </button>
                  </div>
                </div>
              )}
            </CustomerNoForm>
          );
        })}
        <ModalWithItem
          isOpen={isOpenModalNewContact}
          modalTitle={t('label.addNewContact')}
          onToggle={this.onToggleModalNewContact}
          wrapperClass="modal-custom modal-70"
        >
          <FormAddNewContact
            dataField={dataFieldNew}
            checkRoleGroup={checkRoleGroup}
            onAddNewContact={this.onAddNewContact}
            onToggleModal={this.onToggleModalNewContact}
            modeModifyPhone={modeModifyPhone}
            modeAddPhone={modeAddPhone}
            modeDeletePhone={modeDeletePhone}
            ccpPropertyList={this.props.ccpPropertyList}
          />
        </ModalWithItem>
        <ModalAccept
          onAcceptDelete={this.onDeleteContact}
          isOpen={isOpenModalDeleteContact}
          title="Are you sure you want to delete this Contact?"
          onToggle={this.onToggleModalDeleteContact}
        />
      </form>
    );
  }
}

MultiContactForm.propTypes = {
  dataField: PropTypes.arrayOf(PropTypes.object),
  dataFieldNew: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.arrayOf(PropTypes.any),
  modifyAccount: PropTypes.func.isRequired,
  id: PropTypes.string,
  isDisabled: PropTypes.bool,
  isDisableSubmit: PropTypes.bool,
  checkRoleGroup: PropTypes.arrayOf(PropTypes.object),
  modeModifyPhone: PropTypes.number,
  modeAddPhone: PropTypes.number,
  modeDeletePhone: PropTypes.number,
  modeDeleteContact: PropTypes.number,
  modeAddContact: PropTypes.number,
  ccpPropertyList: PropTypes.arrayOf(PropTypes.object),
};

MultiContactForm.defaultProps = {
  id: '',
  dataField: [],
  dataFieldNew: [],
  data: [],
  checkRoleGroup: [],
  isDisabled: false,
  isDisableSubmit: false,
  modeModifyPhone: 2,
  modeAddPhone: 2,
  modeDeletePhone: 2,
  modeDeleteContact: 2,
  modeAddContact: 2,
  ccpPropertyList: [],
};

export default withTranslation('common')(MultiContactForm);
